import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';

export interface SocialMediaPost {
  id: string;
  user_id: string;
  sermon_id: string | null;
  style: string;
  platform: string;
  is_carousel: boolean;
  captions: string[];
  hashtags: string[];
  image_paths: string[];
  archived: boolean;
  created_at: string;
}

interface SavePostData {
  sermon_id?: string;
  style: string;
  platform: string;
  is_carousel: boolean;
  captions: string[];
  hashtags: string[];
  imageBlobs: Blob[];
}

const BUCKET = 'sermon-media';
const SECTION = 'social-media';

export function useSocialMediaPosts(sermonId?: string) {
  const [posts, setPosts] = useState<SocialMediaPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let query = supabase
        .from('social_media_posts')
        .select('*')
        .eq('user_id', user.id)
        .eq('archived', false)
        .order('created_at', { ascending: false });

      if (sermonId) query = query.eq('sermon_id', sermonId);

      const { data, error: fetchError } = await query;
      if (fetchError) throw fetchError;
      setPosts(data || []);
    } catch (err) {
      logger.error('Error loading social media posts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, [sermonId]);

  const getPublicUrl = (path: string) =>
    supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;

  const savePost = async (data: SavePostData): Promise<SocialMediaPost> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const sid = data.sermon_id || 'unsaved';
    const image_paths: string[] = [];

    for (let i = 0; i < data.imageBlobs.length; i++) {
      const blob = data.imageBlobs[i];
      const uuid = crypto.randomUUID();
      const path = `${user.id}/${sid}/${SECTION}/${uuid}.png`;
      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(path, blob, { contentType: 'image/png', upsert: false });
      if (uploadError) throw uploadError;
      image_paths.push(path);
    }

    const { data: row, error: insertError } = await supabase
      .from('social_media_posts')
      .insert([{
        user_id: user.id,
        sermon_id: data.sermon_id || null,
        style: data.style,
        platform: data.platform,
        is_carousel: data.is_carousel,
        captions: data.captions,
        hashtags: data.hashtags,
        image_paths,
      }])
      .select()
      .single();

    if (insertError) throw insertError;
    await loadPosts();
    return row;
  };

  const deletePost = async (post: SocialMediaPost) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    if (post.image_paths?.length) {
      await supabase.storage.from(BUCKET).remove(post.image_paths);
    }

    const { error: deleteError } = await supabase
      .from('social_media_posts')
      .delete()
      .eq('id', post.id)
      .eq('user_id', user.id);

    if (deleteError) throw deleteError;
    await loadPosts();
  };

  const toggleArchive = async (id: string, current: boolean) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');
    await supabase.from('social_media_posts').update({ archived: !current }).eq('id', id).eq('user_id', user.id);
    await loadPosts();
  };

  return { posts, loading, error, loadPosts, savePost, deletePost, toggleArchive, getPublicUrl };
}
